import React, { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { PlusCircle } from "lucide-react";
import { STOCK_UOM_TYPE, TRANSFORM_TYPE } from "../../config/DataFile";

const ItemForm = ({
    data,
    options,
    openModal,
    onSubmit,
    disable,
}) => {
    const initialItemState = {
        name: "",
        itemCompanyId: "",
        unit: "",
        hsn_code: "",
        gst_percentage: 0,
        purchase_rate: 0,
        sale_rate: 0,
        // cost_rate: 0,
        reorder_quantity: 0,
        category: "",
        material_grade: "",
        detail: "",
        in_location: "",
        mcode: "",
        // mocId: "",
        // scheduleId: "",
        // classId: "",
        thickness: "",
        // item_extra_spec: "",
        is_transform: false,
        transform_type: null,
        stock_uom_type: null,
        allow_partial_issue: false,
        allow_recuts: false,
        track_wastage: false,
    };

    const [item, setItem] = useState(initialItemState);
    const [error, setError] = useState({});
    const [thicknessDisabled, setThicknessDisabled] = useState(false);
    const [selectValue, setSelectValue] = useState(true);

    useEffect(() => {
        if (data) {
            setItem({
                ...initialItemState,
                name: data?.name,
                itemCompanyId: data?.itemCompanyId,
                unit: data.unit?._id,
                hsn_code: data?.hsn_code,
                gst_percentage: data?.gst_percentage,
                purchase_rate: data?.purchase_rate,
                sale_rate: data?.sale_rate,
                // cost_rate: location.state?.cost_rate,
                reorder_quantity: data?.reorder_quantity,
                category: data.category?._id,
                material_grade: data?.material_grade,
                detail: data?.detail,
                in_location: data?.location?._id,
                mcode: data.mcode,
                // mocId: location.state?.mocId?._id,
                // scheduleId: location.state?.scheduleId?._id,
                // classId: location.state?.classId?._id,
                thickness: data?.thickness,
                // item_extra_spec: location.state.item_extra_spec,
                is_transform: data?.is_transform ?? false,
                transform_type: data?.transform_type ?? null,
                stock_uom_type: data?.stock_uom_type ?? null,
                allow_partial_issue: data?.allow_partial_issue ?? false,
                allow_recuts: data?.allow_recuts ?? false,
                track_wastage: data?.track_wastage ?? false,
            });
            setSelectValue(data?.status !== undefined ? data?.status : true);
            if (data?.scheduleId || data?.classId) {
                setThicknessDisabled(true);
            }
        }
    }, [data]);

    const handleSwitch = (name) => {
        setItem(prev => ({ ...prev, [name]: !prev[name] }));
    }

    const handleCheckbox = (e) => {
        const checked = e.target.checked;
        setItem(prev => ({
            ...prev,
            is_transform: checked,
            ...(checked
                ? {}
                : {
                    transform_type: null,
                    allow_partial_issue: false,
                    allow_recuts: false,
                    track_wastage: false,
                }),
        }));
    };

    const handleChange = (e) => {
        setItem({ ...item, [e.target.name]: e.target.value });
    };

    const handleDropdownChange = (e, name) => {
        const newItem = { ...item, [name]: e.value };

        if (name === "scheduleId" || name === "classId") {
            const selectedOption =
                name === "scheduleId"
                    ? options?.scheduleData?.find((s) => s._id === e.value)
                    : options?.classData?.find((c) => c._id === e.value);

            if (selectedOption?.thickness) {
                newItem.thickness = selectedOption.thickness;
                setThicknessDisabled(true);
            } else {
                newItem.thickness = "";
                setThicknessDisabled(false);
            }

            if (name === "scheduleId") {
                newItem.classId = "";
            } else if (name === "classId") {
                newItem.scheduleId = "";
            }
        }

        setItem(newItem);
    };

    const handleRadioChange = (event) => {
        setSelectValue(event.target.checked);
    };

    const handleKeyDown = (e) => {
        if (["ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault();
    };

    const validation = () => {
        const requiredFields = {
            name: "Please enter name",
            unit: "Please select unit",
            category: "Please select category",
            in_location: "Please select location",
            hsn_code: "Please enter hsn",
            mcode: "Please enter mcode",
            // mocId: "Please select MOC",
        };

        const errors = {};
        Object.entries(requiredFields).forEach(([field, message]) => {
            if (!item[field]) errors[`${field}_err`] = message;
        });

        setError(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = () => {
        if (validation()) {
            onSubmit(item, selectValue);
        }
    };

    const handleReset = () => {
        setItem(initialItemState);
        setThicknessDisabled(false);
    };

    const renderInputField = (
        label,
        name,
        type = "text",
        required = false,
        extraProps = {}
    ) => (
        <div className="col-12 col-md-4 col-xl-4">
            <div className="input-block local-forms">
                <label>
                    {label} {required && <span className="login-danger">*</span>}
                </label>
                <input
                    className="form-control"
                    type={type}
                    onChange={handleChange}
                    name={name}
                    value={item[name]}
                    disabled={name === "thickness" ? thicknessDisabled : false}
                    {...extraProps}
                />
                {error[`${name}_err`] && (
                    <div className="error">{error[`${name}_err`]}</div>
                )}
            </div>
        </div>
    );

    const renderDropdown = (
        label,
        name,
        dropdownOptions,
        required = false,
        onClick = null,
        disabled = false
    ) => (
        <div className="col-12 col-md-4 col-xl-4">
            <div className="input-block local-forms custom-select-wpr">
                <label>
                    {label} {required && <span className="login-danger">*</span>}
                </label>

                <div className="d-flex gap-2">
                    <div className="flex-grow-1">
                        <Dropdown
                            options={dropdownOptions}
                            value={item[name]}
                            onChange={(e) => handleDropdownChange(e, name)}
                            filter
                            className="w-100"
                            placeholder={`Select ${label}`}
                            disabled={disabled}
                        />
                    </div>

                    {onClick && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-1 d-flex align-items-center justify-content-center"
                            style={{ width: "40px", height: "38px" }}
                            data-bs-toggle="tooltip"
                            data-bs-title={`Add ${label}`}
                            onClick={onClick}
                        >
                            <PlusCircle size={16} />
                        </button>
                    )}
                </div>
                {error[`${name}_err`] && (
                    <div className="error">{error[`${name}_err`]}</div>
                )}
            </div>
        </div>
    );

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <form>
                                <div className="col-12">
                                    <div className="form-heading">
                                        <h4>{data?._id ? "Edit" : "Add"} Item Details</h4>
                                    </div>
                                </div>

                                <div className="row">
                                    {renderInputField("Name", "name", "text", true)}
                                    {renderDropdown(
                                        "Item Company Name",
                                        "itemCompanyId",
                                        options?.itemCompanyOption
                                    )}
                                    {renderDropdown("Unit", "unit", options?.unitOption, true, () =>
                                        openModal("unit")
                                    )}
                                    {renderDropdown(
                                        "Category",
                                        "category",
                                        options?.catOptions,
                                        true,
                                        () => openModal("category")
                                    )}
                                    {renderInputField("HSN", "hsn_code", "number", true)}
                                    {renderInputField("Grade", "material_grade")}
                                    {renderInputField("MCode", "mcode", "text", true)}
                                    {/* {renderDropdown("Schedule", "scheduleId", options.scheduleOptions, true)}
                                    {renderDropdown("Class", "classId", options.classOptions, true)}
                                    {renderDropdown("MOC", "mocId", options.mocOptions, true)} */}
                                    {renderInputField("Thickness", "thickness", "text")}
                                    {renderInputField(
                                        "GST(%)",
                                        "gst_percentage",
                                        "number",
                                        false,
                                        {
                                            onWheel: (e) => e.target.blur(),
                                            onKeyDown: handleKeyDown,
                                        }
                                    )}
                                    {renderInputField(
                                        "Purchase Rate",
                                        "purchase_rate",
                                        "number",
                                        false,
                                        {
                                            onWheel: (e) => e.target.blur(),
                                            onKeyDown: handleKeyDown,
                                        }
                                    )}
                                    {renderInputField(
                                        "Sale Rate",
                                        "sale_rate",
                                        "number",
                                        false,
                                        {
                                            onWheel: (e) => e.target.blur(),
                                            onKeyDown: handleKeyDown,
                                        }
                                    )}
                                    {/* {renderInputField("Cost", "cost_rate", "number", false, {
                                            onWheel: (e) => e.target.blur(),
                                            onKeyDown: handleKeyDown
                                        })} */}
                                    {renderInputField(
                                        "Reorder Quantity",
                                        "reorder_quantity",
                                        "number",
                                        false,
                                        {
                                            onWheel: (e) => e.target.blur(),
                                            onKeyDown: handleKeyDown,
                                        }
                                    )}
                                    {renderDropdown(
                                        "Location",
                                        "in_location",
                                        options?.locationOptions,
                                        true,
                                        () => openModal("location")
                                    )}
                                    {/* {renderDropdown("Item (Seamless/ERW)", "item_extra_spec", options?.itemSpecData, true)} */}

                                    <div className="col-12">
                                        <div className="card border-0">
                                            <div className="card-body p-0">

                                                {/* Title */}
                                                {/* <div className="d-flex align-items-center justify-content-between mb-4">
                                                    <h4 className="mb-0">Transformation Configuration</h4>
                                                </div> */}

                                                {/* is transform */}
                                                <div className="" style={{ marginBottom: "35px", maxWidth: "400px" }}>
                                                    <div className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="is_transform"
                                                            checked={item.is_transform}
                                                            onChange={handleCheckbox}
                                                        />
                                                        <label className="form-check-label fw-semibold d-flex flex-column" htmlFor="is_transform">
                                                            Transformable Item
                                                            <span className="text-muted small"> (Enable to configure item for fabrication processes)</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {/* dependent fields */}
                                                <div className={` ${!item.is_transform ? "opacity-50 pointer-events-none" : ""}`}>
                                                    <div className="row">

                                                        {/* transform type */}
                                                        {renderDropdown(
                                                            "Transform Type",
                                                            "transform_type",
                                                            TRANSFORM_TYPE,
                                                            false,
                                                            null,
                                                            !item.is_transform
                                                        )}

                                                        {renderDropdown(
                                                            "Stock UOM Type",
                                                            "stock_uom_type",
                                                            STOCK_UOM_TYPE,
                                                            false,
                                                            null,
                                                            !item.is_transform
                                                        )}

                                                    </div>

                                                    {/* switches */}
                                                    <div className="mb-3">
                                                        <label className="fw-semibold d-block mb-2">
                                                            Issue & Cutting Rules
                                                        </label>

                                                        <div className="row">
                                                            <div className="col-md-3">
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={item.allow_partial_issue}
                                                                        disabled={!item.is_transform}
                                                                        onChange={() => handleSwitch("allow_partial_issue")}
                                                                    />
                                                                    <label className="form-check-label">
                                                                        Allow Partial Issue
                                                                    </label>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={item.allow_recuts}
                                                                        disabled={!item.is_transform}
                                                                        onChange={() => handleSwitch("allow_recuts")}
                                                                    />
                                                                    <label className="form-check-label">
                                                                        Allow Recuts
                                                                    </label>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-3">
                                                                <div className="form-check form-switch">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        checked={item.track_wastage}
                                                                        disabled={!item.is_transform}
                                                                        onChange={() => handleSwitch("track_wastage")}
                                                                    />
                                                                    <label className="form-check-label">
                                                                        Track Wastage
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>

                                    {data?._id ? (
                                        <div className="col-12 col-md-4 col-xl-4">
                                            <div className="cardNum">
                                                <div className="mb-3">
                                                    <label
                                                        htmlFor="fileUpload"
                                                        className="form-label"
                                                    >
                                                        Status
                                                    </label>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            role="switch"
                                                            onChange={handleRadioChange}
                                                            checked={selectValue}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}

                                    <div className="col-12 col-md-12 col-xl-12">
                                        <div className="input-block local-forms">
                                            <label> Details</label>
                                            <textarea
                                                className="form-control"
                                                type="text"
                                                onChange={handleChange}
                                                name="detail"
                                                value={item.detail}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                            <div className="col-12">
                                <div className="doctor-submit text-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary submit-form me-2"
                                        onClick={handleSubmit}
                                        disabled={disable}
                                    >
                                        {disable
                                            ? "Processing..."
                                            : data?._id
                                                ? "Update"
                                                : "Submit"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary cancel-form"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >
            </div >
        </>
    );
};

export default ItemForm;
